import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Database, Cookie, Send, Lock, Mail } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/lib/site";

const ICONS = [Shield, Database, Cookie, Send, Lock];

const Datenschutz = () => {
    const { t } = useLanguage();

    useEffect(() => {
        window.scrollTo({ top: 0 });
        const seo = t.page_meta?.seo?.datenschutz;
        if (seo) document.title = seo.title;
    }, [t]);

    const D = t.legal.datenschutz;

    return (
        <div data-testid="datenschutz-page" className="min-h-screen bg-ella-cream">
            <Header />
            <main className="pt-32 md:pt-40 pb-24">
                <div className="ella-container max-w-3xl">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ella-muted hover:text-ella-ink transition-colors"
                        data-testid="legal-back"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {t.legal.back}
                    </Link>

                    <span className="ella-overline mt-12 block">ELLA Online Coaching</span>
                    <h1
                        className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-3 text-ella-ink leading-[1.05]"
                        data-testid="datenschutz-title"
                    >
                        {D.title}
                    </h1>
                    <p className="mt-5 text-ella-ink/65 italic font-light leading-relaxed">
                        {D.intro}
                    </p>

                    <div className="mt-10 h-px w-24 bg-ella-gold" />

                    {/* Section 0: responsable */}
                    <section className="mt-14">
                        <p className="ella-overline">{D.controller_label}</p>
                        <p className="font-display text-xl md:text-2xl text-ella-ink mt-2 leading-tight">
                            {SITE.brand} · {SITE.coachName}
                        </p>
                        <p className="mt-2 text-sm text-ella-ink/70 inline-flex items-center gap-2">
                            <Mail className="w-4 h-4 text-ella-gold-dark" strokeWidth={1.5} />
                            <a
                                href={`mailto:${SITE.contact.email}`}
                                className="hover:text-ella-gold-dark transition-colors"
                            >
                                {SITE.contact.email}
                            </a>
                        </p>
                    </section>

                    {/* Sections */}
                    <ol className="mt-14 space-y-12">
                        {(D.sections || []).map((s, idx) => {
                            const Icon = ICONS[idx % ICONS.length];
                            return (
                                <li
                                    key={idx}
                                    data-testid={`datenschutz-section-${idx}`}
                                >
                                    <header className="flex items-start gap-4">
                                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-ella-ink text-ella-gold flex-shrink-0">
                                            <Icon className="w-5 h-5" strokeWidth={1.4} />
                                        </span>
                                        <div>
                                            <p className="ella-overline">
                                                0{idx + 1}
                                            </p>
                                            <h2 className="font-display text-2xl md:text-3xl text-ella-ink leading-tight mt-1">
                                                {s.heading}
                                            </h2>
                                        </div>
                                    </header>
                                    <div className="pl-16 mt-5 space-y-4 text-ella-ink/85 leading-relaxed font-light text-[15.5px]">
                                        {s.body.map((p, i) => (
                                            <p key={i}>{p}</p>
                                        ))}
                                        {s.list && (
                                            <ul className="space-y-2 pl-4 mt-2 list-disc marker:text-ella-gold-dark">
                                                {s.list.map((it, i) => (
                                                    <li key={i}>{it}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>

                    {/* Rights box */}
                    <section className="mt-16 rounded-3xl border border-ella-gold/30 bg-white p-8 md:p-10">
                        <p className="ella-overline">{D.rights_label}</p>
                        <h3 className="font-display text-2xl md:text-3xl text-ella-ink mt-2 leading-tight">
                            {D.rights_title}
                        </h3>
                        <p className="mt-4 text-ella-ink/80 font-light leading-relaxed text-[15.5px]">
                            {D.rights_body}
                        </p>
                        <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                            {D.rights_items.map((r, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-2 text-sm text-ella-ink/85"
                                >
                                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-ella-gold flex-shrink-0" />
                                    {r}
                                </li>
                            ))}
                        </ul>
                        <a
                            href={`mailto:${SITE.contact.email}?subject=Datenschutz%20Anfrage`}
                            data-testid="datenschutz-contact-cta"
                            className="mt-7 inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-ella-ink text-ella-cream hover:bg-ella-gold-dark transition-colors text-xs uppercase tracking-[0.24em]"
                        >
                            <Mail className="w-4 h-4" strokeWidth={1.5} />
                            {D.rights_cta}
                        </a>
                    </section>

                    <p className="mt-16 pt-10 border-t border-ella-line italic text-ella-muted text-sm leading-relaxed">
                        © {new Date().getFullYear()} ELLA Online Coaching ·{" "}
                        {SITE.coachName} · {D.last_updated}
                    </p>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Datenschutz;
